import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { expertise } from "@/data/skills";
import { useIsDesktop, useIsMobile } from "@/hooks/useDevice";

export function Expertise() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  const toggle = (i: number) => setOpenIndex((cur) => (cur === i ? null : i));

  return (
    <section id="expertise" className="container-x section-y" data-testid="expertise-section">
      <p className="eyebrow">02 — EXPERTISE</p>
      <div className="mt-14 border-t border-line">
        {expertise.map((area, i) => {
          const open = openIndex === i;
          return (
            <motion.div
              key={area.index}
              className="relative border-b border-line"
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={isDesktop ? () => setOpenIndex(i) : undefined}
              onMouseLeave={isDesktop ? () => setOpenIndex(null) : undefined}
              data-testid={`expertise-row-${area.index}`}
            >
              <button
                className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-5 py-7 text-left md:gap-10 md:py-9"
                onClick={() => !isDesktop && toggle(i)}
                aria-expanded={open}
                data-testid={`expertise-toggle-${area.index}`}
              >
                <span className="font-mono text-xs text-accent">{area.index}</span>
                <span
                  className="font-display text-xl font-semibold tracking-tight transition-transform duration-500 md:text-4xl"
                  style={{ transform: open ? "translateX(12px)" : undefined }}
                >
                  {area.title}
                </span>
                <span className="flex items-center gap-3 text-muted">
                  {isMobile ? (
                    <Plus size={18} className={`transition-transform duration-300 ${open ? "rotate-45" : ""}`} aria-hidden="true" />
                  ) : (
                    <ArrowUpRight
                      size={22}
                      className="transition-all duration-500"
                      style={{ transform: open ? "translate(4px, -4px)" : undefined, color: open ? "var(--accent)" : undefined }}
                      aria-hidden="true"
                    />
                  )}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="grid gap-6 pb-9 md:grid-cols-2 md:pl-[4.5rem]">
                      <p className="max-w-md text-sm leading-relaxed text-muted" data-testid={`expertise-description-${area.index}`}>
                        {area.description}
                      </p>
                      <ul className="flex flex-wrap content-start gap-2">
                        {area.items.map((item) => (
                          <li key={item} className="border border-line px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] text-foreground/80">
                            {item.toUpperCase()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <span
                className="pointer-events-none absolute bottom-0 left-0 h-px w-full origin-left transition-transform duration-500"
                style={{ backgroundColor: "var(--accent)", transform: open ? "scaleX(1)" : "scaleX(0)" }}
                aria-hidden="true"
              />
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
